// Dev mode 配对页：手动录入 device_id + token + gw_url。
//
// 真正的配对协议（短期 token + 6 位 PIN + ECDH P-256）留 Day-7 实施。
// Day-4 不需要任何加密交换：phone 复用 Gateway 已知的 EAR_DEV_TOKENS 表里的某项。
//
// 用例：先用这个跑通 PWA → /api/agents → /ws/phone 全链路，
// 再去 Day-7 换成扫码 + PIN 配对页。
//
// 验证步骤：填完 → 点"测试 + 保存"
//   1. 用填的 token 对 GET /api/ping 签名
//   2. 200 → 写 IndexedDB 跳主页；非 200 → 红条报错

import { useState } from "preact/hooks";
import { signRequest } from "../crypto/hmac";
import { tokenStore } from "../storage/tokens";

const DEFAULT_GW = "https://ear-sgp.platform-1.xyz";

export function PairingPageDev({ onPaired }: { onPaired: () => void }) {
  const [deviceId, setDeviceId] = useState("phone-iphone-1");
  const [token, setToken] = useState("");
  const [alias, setAlias] = useState("📱 我的手机");
  const [gwUrl, setGwUrl] = useState(DEFAULT_GW);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function onTestSave(e: Event) {
    e.preventDefault();
    setErr(null);
    setOk(null);
    if (!deviceId.trim() || !token.trim()) {
      setErr("device_id 和 token 都必填");
      return;
    }
    setBusy(true);
    try {
      // 用即将保存的 token 签 /api/ping 一次，验证有效
      const { authHeaderValue } = await signRequest(
        deviceId.trim(),
        token.trim(),
        "GET",
        "/api/ping",
        null
      );
      const url = new URL("/api/ping", gwUrl).toString();
      const resp = await fetch(url, {
        headers: { "X-Auth-Ear": authHeaderValue }
      });
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${body.slice(0, 200)}`);
      }
      const j = (await resp.json()) as { pong: boolean; device_id: string };
      if (!j.pong || j.device_id !== deviceId.trim()) {
        throw new Error(`unexpected response: ${JSON.stringify(j)}`);
      }
      // 通过 → 落库
      await tokenStore.setIdentity({
        device_id: deviceId.trim(),
        token: token.trim(),
        alias: alias.trim() || "📱",
        gw_url: gwUrl.trim().replace(/\/$/, ""),
        created_at: Date.now()
      });
      setOk("✓ 验证通过，已保存");
      setTimeout(onPaired, 600);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="page page-pair">
      <header class="page-pair__header">
        <h1>开发模式配对</h1>
        <p class="muted">
          手动录入 Gateway 已知的 device_id + token，用于 Day-4 端到端联调。
          <br />
          正式配对协议（PIN + ECDH）将在 Day-7 启用。
        </p>
      </header>

      <form class="pair-form" onSubmit={onTestSave}>
        <label>
          <span>device_id</span>
          <input
            value={deviceId}
            onInput={(e) => setDeviceId((e.target as HTMLInputElement).value)}
            placeholder="phone-iphone-1"
            autocomplete="off"
            spellcheck={false}
          />
        </label>
        <label>
          <span>token (HMAC secret)</span>
          <input
            type="password"
            value={token}
            onInput={(e) => setToken((e.target as HTMLInputElement).value)}
            placeholder="对应 GW EAR_DEV_TOKENS 里的 value"
            autocomplete="off"
            spellcheck={false}
          />
        </label>
        <label>
          <span>别名（emoji 可选）</span>
          <input
            value={alias}
            onInput={(e) => setAlias((e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          <span>Gateway URL</span>
          <input
            value={gwUrl}
            onInput={(e) => setGwUrl((e.target as HTMLInputElement).value)}
          />
        </label>

        <button type="submit" class="btn btn-primary" disabled={busy}>
          {busy ? "验证中…" : "测试 + 保存"}
        </button>

        {err && <div class="alert alert-err">{err}</div>}
        {ok && <div class="alert alert-ok">{ok}</div>}
      </form>

      <footer class="page-pair__footer muted">
        <small>token 仅存在浏览器本地 IndexedDB；可在主页右上角"退出"清除。</small>
      </footer>
    </div>
  );
}
